import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { connect, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Form, Input } from "reactstrap";
import Popup from "reactjs-popup";
import BreadCrumbs from "../../components/breadCrumbs";
import SideNav from "../../components/sideNav";
import TopNav from "../../components/topNav";
import envConfig from "../../config/constants";
import { checkEditAccessRight } from "../../config/utility";
import {
  createWipProject,
  getWipProjects,
  getWipRollForward,
  importWipProjects,
} from "../../redux/api";
import { setMessageUser } from "../../redux/features/user";
import { getAuthKey, getUserData } from "../../redux/selector";
import { cross } from "../../config/assets";
import {
  openingDateToYear,
  openingYearToDate,
  parseWipCsv,
  WIP_CSV_HEADERS,
} from "./helpers";
import "./wip.scss";

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const emptyProject = {
  name: "",
  opening_balance: "0",
  opening_year: "",
};
const emptyImport = { errors: [], projects: [], fileName: "" };

const Wip = (props) => {
  const dispatch = useDispatch();
  const isReadOnly = checkEditAccessRight(props.userData.access_right);
  const [projects, setProjects] = useState([]);
  const [matrix, setMatrix] = useState({ projects: [], rows: [] });
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [project, setProject] = useState(emptyProject);
  const [importData, setImportData] = useState(emptyImport);
  const financialYearStart = props.userData.organization.fy_start;
  const maximumOpeningYear = openingDateToYear(financialYearStart);

  const customStyles = {
    headCells: {
      style: {
        fontWeight: "600",
        fontSize: "15px",
        textTransform: "capitalize",
      },
    },
    rows: { style: { minHeight: "3rem" } },
  };

  const load = useCallback(async () => {
    const [projectResponse, matrixResponse] = await Promise.all([
      dispatch(getWipProjects({ authKey: props.authKey })),
      dispatch(getWipRollForward({ authKey: props.authKey })),
    ]);
    if (projectResponse.payload?.status === 200)
      setProjects(projectResponse.payload.data);
    if (matrixResponse.payload?.status === 200)
      setMatrix(matrixResponse.payload.data);
  }, [dispatch, props.authKey]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (response) =>
    props.setMessageUser({
      message: response.payload?.message || "Request failed",
      type:
        response.payload?.status === 200
          ? envConfig.notificationTypes.success
          : envConfig.notificationTypes.danger,
    });

  const matrixColumns = useMemo(
    () => [
      {
        name: "Financial Year",
        selector: (row) => row.financial_year,
        minWidth: "135px",
      },
      {
        name: "Opening WIP",
        cell: (row) => money(row.opening_wip),
        right: true,
        minWidth: "130px",
      },
      ...matrix.projects.map((item) => ({
        name: item.name,
        cell: (row) =>
          money(
            row.projects.find(
              (entry) => Number(entry.project_id) === Number(item.id),
            )?.amount,
          ),
        right: true,
        minWidth: "145px",
      })),
      {
        name: "Additions During the Year",
        cell: (row) => money(row.additions),
        right: true,
        minWidth: "190px",
      },
      {
        name: "Transfers to PPE",
        cell: (row) => money(row.transfers),
        right: true,
        minWidth: "150px",
      },
      {
        name: "Closing WIP",
        cell: (row) => money(row.closing_wip),
        right: true,
        minWidth: "130px",
      },
    ],
    [matrix.projects],
  );

  const downloadSample = () => {
    const openingYear = maximumOpeningYear || new Date().getFullYear();
    const openingDate = openingYearToDate(openingYear, financialYearStart);
    const rows = [WIP_CSV_HEADERS.join(",")];

    for (let projectNumber = 1; projectNumber <= 25; projectNumber += 1) {
      const projectName = `Test WIP Project ${String(projectNumber).padStart(2, "0")}`;
      for (let invoiceNumber = 1; invoiceNumber <= 20; invoiceNumber += 1) {
        rows.push(
          [
            projectName,
            invoiceNumber === 1 ? "1000" : "",
            invoiceNumber === 1 ? openingYear : "",
            `WIP-${String(projectNumber).padStart(2, "0")}-${String(invoiceNumber).padStart(3, "0")}`,
            openingDate,
            `Test Supplier ${projectNumber}`,
            `Test invoice ${invoiceNumber} for project ${projectNumber}`,
            String(100 + invoiceNumber),
          ].join(","),
        );
      }
    }
    const sample = rows.join("\r\n");
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${sample}`], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "WIP_Import_Sample.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const readImportFile = (file) => {
    if (!file) {
      setImportData(emptyImport);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setImportData({
        errors: [{ row: 1, message: "Select a CSV file" }],
        projects: [],
        fileName: file.name,
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseWipCsv(reader.result, projects, financialYearStart);
      setImportData({ ...parsed, fileName: file.name });
    };
    reader.onerror = () =>
      setImportData({
        errors: [{ row: 1, message: "The CSV file could not be read" }],
        projects: [],
        fileName: file.name,
      });
    reader.readAsText(file);
  };

  return (
    <>
      <TopNav />
      <SideNav />
      <div className="listings">
        <BreadCrumbs crumbs={["Work in Progress"]} />
        <div className="wip-page wip-page--overview">
          <div className="wip-page__toolbar">
            <h3 className="heading-3">Work in Progress</h3>
            <div className="wip-page__actions">
              {!isReadOnly && (
                <>
                  <Button
                    className="btn btn__border btn__brd"
                    onClick={() => {
                      setImportData(emptyImport);
                      setShowImport(true);
                    }}
                  >
                    Import Projects
                  </Button>
                  <Button
                    className="btn btn__blue btn__brd"
                    onClick={() => {
                      setProject(emptyProject);
                      setShowForm(true);
                    }}
                  >
                    + New Project
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="wip-overview">
            <section className="wip-card wip-projects">
              <h4>Projects</h4>
              <div className="wip-projects__list">
                {projects.map((item) => (
                  <Link
                    className="wip-project"
                    to={`/app/wip/${item.id}`}
                    key={item.id}
                  >
                    <span className="wip-project__heading">
                      <strong>{item.name}</strong>
                      <small>{item.status.replaceAll("_", " ")}</small>
                    </span>
                    <strong>Total WIP: {money(item.total_wip)}</strong>
                  </Link>
                ))}
                {!projects.length && <p>No WIP projects found.</p>}
              </div>
            </section>
            <section
              className="wip-card wip-roll-forward"
              aria-label="Work in progress roll-forward"
              tabIndex="0"
            >
              <h4>WIP Roll-forward</h4>
              <DataTable
                columns={matrixColumns}
                data={matrix.rows}
                highlightOnHover
                pagination
                persistTableHead
                customStyles={customStyles}
                noDataComponent="Create a project to begin tracking WIP."
              />
            </section>
          </div>
        </div>
      </div>

      <Popup className="wip-popup" open={showForm} closeOnDocumentClick={false}>
        <div className="modal popup__modal wip-modal">
          <div className="header popup__header mb-sm">
            <h2 className="heading-2 popup__blue">New WIP Project</h2>
            <button
              className="popup__close close"
              type="button"
              onClick={() => {
                setProject(emptyProject);
                setShowForm(false);
              }}
            >
              <img src={cross} alt="Close" />
            </button>
          </div>
          <Form
            className="wip-popup-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const response = await dispatch(
                createWipProject({
                  authKey: props.authKey,
                  body: JSON.stringify({
                    name: project.name,
                    opening_balance: Number(project.opening_balance),
                    opening_balance_date: openingYearToDate(
                      project.opening_year,
                      financialYearStart,
                    ),
                  }),
                }),
              );
              notify(response);
              if (response.payload?.status === 200) {
                setProject(emptyProject);
                setShowForm(false);
                await load();
              }
            }}
          >
            <label className="form__label">
              Project name
              <Input
                className="form__input"
                required
                maxLength={100}
                value={project.name}
                onChange={(event) =>
                  setProject({ ...project, name: event.target.value })
                }
              />
            </label>
            <label className="form__label">
              Opening balance
              <Input
                className="form__input"
                required
                min="0"
                step="0.01"
                type="number"
                value={project.opening_balance}
                onChange={(event) =>
                  setProject({
                    ...project,
                    opening_balance: event.target.value,
                  })
                }
              />
            </label>
            <label className="form__label">
              Opening financial year
              <Input
                className="form__input"
                required
                min="1900"
                max={maximumOpeningYear}
                type="number"
                placeholder="YYYY"
                value={project.opening_year}
                onChange={(event) =>
                  setProject({ ...project, opening_year: event.target.value })
                }
              />
            </label>
            <div className="popup__btns">
              <Button
                className="btn btn__border popup__btn"
                type="button"
                onClick={() => {
                  setProject(emptyProject);
                  setShowForm(false);
                }}
              >
                Cancel
              </Button>
              <Button className="btn btn__blue popup__btn" type="submit">
                Create Project
              </Button>
            </div>
          </Form>
        </div>
      </Popup>

      <Popup
        className="wip-popup"
        open={showImport}
        closeOnDocumentClick={false}
      >
        <div className="modal popup__modal wip-modal wip-import">
          <div className="header popup__header mb-sm">
            <h2 className="heading-2 popup__blue">Import WIP Projects</h2>
            <button
              className="popup__close close"
              type="button"
              onClick={() => {
                setImportData(emptyImport);
                setShowImport(false);
              }}
            >
              <img src={cross} alt="Close" />
            </button>
          </div>
          <p>
            Select a CSV using the supplied format. The complete file is
            validated before any project is created.
          </p>
          <Button
            className="btn btn__border btn__brd"
            type="button"
            onClick={downloadSample}
          >
            Download Sample CSV
          </Button>
          <label className="form__label wip-import__file">
            CSV file
            <Input
              className="form__input"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => readImportFile(event.target.files?.[0])}
            />
          </label>
          {importData.fileName && !importData.errors.length && (
            <div className="wip-import__summary">
              <strong>{importData.fileName}</strong>
              <span>{importData.projects.length} projects</span>
              <span>
                {importData.projects.reduce(
                  (count, item) => count + item.invoices.length,
                  0,
                )}{" "}
                invoices
              </span>
            </div>
          )}
          {importData.errors.length > 0 && (
            <div className="wip-import__errors" role="alert">
              <strong>Correct these CSV errors:</strong>
              <ul>
                {importData.errors.map((error, index) => (
                  <li key={`${error.row}-${index}`}>
                    Row {error.row}: {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="popup__btns">
            <Button
              className="btn btn__border popup__btn"
              type="button"
              onClick={() => setShowImport(false)}
            >
              Cancel
            </Button>
            <Button
              className="btn btn__blue popup__btn"
              type="button"
              disabled={
                importData.projects.length === 0 || importData.errors.length > 0
              }
              onClick={async () => {
                const response = await dispatch(
                  importWipProjects({
                    authKey: props.authKey,
                    body: JSON.stringify({ projects: importData.projects }),
                  }),
                );
                notify(response);
                if (response.payload?.status === 200) {
                  setImportData(emptyImport);
                  setShowImport(false);
                  await load();
                } else if (Array.isArray(response.payload?.errors)) {
                  setImportData((current) => ({
                    ...current,
                    errors: response.payload.errors.map((error) => ({
                      row: error.project || "Server",
                      message: error.message,
                    })),
                  }));
                }
              }}
            >
              Import Projects
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default connect(
  (state) => ({ authKey: getAuthKey(state), userData: getUserData(state) }),
  { setMessageUser },
)(Wip);
